import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Form, Row, Col, notification, Divider, Spin, Space } from 'antd'
import { LoginForm, ConfirmPasswordForm, EmailConfirmForm } from 'modules/login/components'
import { Button } from 'modules/core/components'
import { useTranslation } from 'react-i18next'
import { AssociatedAccountModal, CodeModal, LoginOtherContainer } from './components'
import { tokenutils } from 'modules/login/actions'
import {
  /*GA,*/ generateMessageError,
  renderLoginAndResetFormTitle,
  renderLoginWelcome,
} from 'modules/core/utils'
import { onSubmitChange, onSubmitLogin } from './utils'
import login from 'modules/login'

import './Login.scss'
import { useFeatureValue } from '@growthbook/growthbook-react'

const Login = ({
  history,
  onLogin,
  onLogout,
  logout,
  whoAmI,
  confirmPassword,
  loggedUser: { pkid },
  googleCallback,
  fetchLoginOtherAccounts,
  azureCallback,
  sendEmailSelfResetPassword,
  setAuthenticatedTrue,
}) => {
  const tenantIdMicrosoftLogin = useFeatureValue('enable-miscrosoft-login', { tenantId: null })
  const dominioMicrosoftLogin = useFeatureValue('dominio-microsoft-login', {
    cliente: null,
    tenantId: null,
  })
  const [isLogging, setIsLogging] = useState(false)
  const [showConfirmPasswordForm, setShowConfirmPasswordForm] = useState(false)
  const [showEmailConfirmForm, setShowEmailConfirmForm] = useState(false)
  const [isLoggingOther, setIsLoggingOther] = useState(false)
  const [showAccountsModal, setShowAccountsModal] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const [form] = Form.useForm()
  const { t } = useTranslation()

  var queryString = window.location.search
  var urlParams = new URLSearchParams(queryString)
  var code = urlParams.get('code')
  var error = urlParams.get('error')
  var state = urlParams.get('state')
  var tenantId = urlParams.get('tenant_id')

  const domain = window.location.host
  const subdomain = domain.split('.')[0]

  // Función para determinar el provider basándose en el state
  const determineProvider = (stateParam) => {
    if (!stateParam) return null

    try {
      // Decodificar URL encoding primero
      let decodedState = decodeURIComponent(stateParam)

      // Verificar si ya es JSON válido antes de intentar base64
      let stateObject
      let isJson = false

      try {
        stateObject = JSON.parse(decodedState)
        isJson = true
      } catch (e) {
        // Si no es JSON válido, intentar decodificar como base64
        try {
          decodedState = Buffer.from(decodedState, 'base64').toString()
          // Intentar parsear como JSON después de base64
          try {
            stateObject = JSON.parse(decodedState)
            isJson = true
          } catch (e2) {
            // Si aún no es JSON válido, continuar con lógica de string simple
          }
        } catch (e3) {
          // Si no es base64, continuar con lógica de string simple
        }
      }

      // Si es un objeto JSON, aplicar lógica de JSON
      if (isJson && stateObject) {
        // Buscar el atributo provider
        if (stateObject.provider) {
          return stateObject.provider.toLowerCase()
        }

        // Verificar otros atributos que puedan indicar el provider
        if (stateObject.type === 'azure' || stateObject.type === 'microsoft') {
          return 'azure'
        }
        if (stateObject.type === 'google') {
          return 'google'
        }

        // Detectar Azure basándose en la presencia de tenant_id
        if (stateObject.tenant_id) {
          return 'azure'
        }

        // Detectar Google basándose en la presencia de indicadores típicos
        if (stateObject.client_id && stateObject.client_id.includes('google')) {
          return 'google'
        }

        // Verificar otros indicadores específicos de Google OAuth
        if (stateObject.client_id && stateObject.client_id.includes('apps.googleusercontent.com')) {
          return 'google'
        }

        // Verificar scopes típicos de Google
        if (stateObject.scope) {
          const googleScopes = ['email', 'profile', 'openid', 'https://www.googleapis.com']
          const hasGoogleScope = googleScopes.some((scope) =>
            stateObject.scope.toLowerCase().includes(scope.toLowerCase()),
          )
          if (hasGoogleScope) {
            return 'google'
          }
        }

        // Verificar response_type típico de OAuth 2.0 (Google)
        if (stateObject.response_type === 'code') {
          return 'google'
        }

        // Verificar access_type típico de Google
        if (stateObject.access_type === 'offline' || stateObject.access_type === 'online') {
          return 'google'
        }

        // Verificar prompt típico de Google
        if (stateObject.prompt === 'consent' || stateObject.prompt === 'select_account') {
          return 'google'
        }

        // Si no hay indicadores específicos en JSON, retornar null
        return null
      }

      // Verificar si es base64 válido (típico de Google)
      const isBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(stateParam)
      if (isBase64) {
        return 'google'
      }

      // Si es un string simple y alfanumérico (no base64), también podría ser Google
      if (/^[a-zA-Z0-9]+$/.test(stateParam)) {
        return 'google'
      }

      // Si contiene caracteres especiales pero no es JSON válido, podría ser Azure
      if (/[{}":,\[\]]/.test(stateParam)) {
        return 'azure'
      }

      // Verificar si contiene indicadores de provider en texto plano
      if (
        decodedState.toLowerCase().includes('azure') ||
        decodedState.toLowerCase().includes('microsoft')
      ) {
        return 'azure'
      }
      if (decodedState.toLowerCase().includes('google')) {
        return 'google'
      }

      // Si no se puede determinar, retornar null
      return null
    } catch (error) {
      console.error('Error parsing state:', error)
      return null
    }
  }

  // Función para determinar qué callback usar
  const getCallbackAction = (codeParam, stateParam, errorParam) => {
    const provider = determineProvider(stateParam)

    if (provider === 'azure') {
      return () => azureCallback({ code: codeParam, state: stateParam, error: errorParam })
    } else if (provider === 'google') {
      return () => googleCallback({ code: codeParam, error: errorParam, state: stateParam })
    } else {
      // Fallback a la lógica original si no se puede determinar el provider
      return codeParam && stateParam
        ? () => googleCallback({ code: codeParam, error: errorParam, state: stateParam })
        : () => azureCallback({ code: codeParam, state: stateParam, error: errorParam })
    }
  }

  useEffect(() => {
    return () => {
      setShowEmailConfirmForm(false)
      setShowConfirmPasswordForm(false)
      setIsLogging(false)
      setIsLoggingOther(false)
      setShowAccountsModal(false)
    }
  }, [])

  useEffect(() => {
    if (code || state || error) {
      setIsLoggingOther(true)
      const actionAux = getCallbackAction(code, state, error)
      whoAmI().then((resp) => {
        if (resp.error.response.status === 401) {
          tokenutils.removeToken()
        }
        actionAux()
          .then((response) => {
            console.log('🔍 Debug - SSO login successful, saving tokens')
            tokenutils.saveTokens(response.payload.data)
            // Obtener datos del usuario primero, luego establecer autenticación
            Promise.all([
              whoAmI().catch((error) => {
                console.error('🔍 Debug - whoAmI error:', error)
                return null
              }),
              fetchLoginOtherAccounts(),
            ])
              .then(([whoAmIResponse, responseAccount]) => {
                console.log('🔍 Debug - whoAmI response:', whoAmIResponse)
                console.log('🔍 Debug - Associated accounts:', responseAccount.payload.data.length)

                // Establecer autenticación después de que whoAmI termine
                setAuthenticatedTrue()
                console.log('🔍 Debug - Setting isAuthenticated to true after whoAmI')

                if (responseAccount.payload.data.length > 1) {
                  console.log('🔍 Debug - Multiple accounts, showing modal')
                  setShowAccountsModal(true)
                } else {
                  // Para SSO (Google/Azure), no mostrar modal de código QR automáticamente
                  // Solo redirigir al usuario a la aplicación
                  console.log('🔍 Debug - Single account, redirecting to /')
                  setIsLoggingOther(false)
                  console.log('🔍 Debug - isLoggingOther:', isLoggingOther)
                  // Pequeño delay para asegurar que el estado se actualice
                  setTimeout(() => {
                    history.push('/')
                    console.log('🔍 Debug - Redirect executed')
                  }, 100)
                }
              })
              .catch((error) => {
                console.error('🔍 Debug - Error in Promise.all:', error)
                // Si hay error, intentar redirigir de todas formas
                setIsLoggingOther(false)
                history.push('/')
              })
          })
          .catch((error) => {
            notification.error({
              message: t('LOGIN_USER_NOT_FOUND_FEEDBACK_TITLE'),
              description: generateMessageError(error, 'LOGIN_USER_NOT_FOUND_FEEDBACK_DESCRIPTION'),
              duration: 0,
            })
            setIsLoggingOther(false)
          })
      })
    }
    // eslint-disable-next-line
  }, [googleCallback, azureCallback, code, error, state])

  const onConfirm = () => {
    form.resetFields()
    setShowConfirmPasswordForm(true)
    setIsLogging(false)
  }

  const renderConfirmOrLoginForm = () => (
    <>
      {renderLoginAndResetFormTitle({
        title: showConfirmPasswordForm ? 'LOGIN_FORM_TITLE_CREATE_PASSWORD' : 'LOGIN_FORM_TITLE',
        level: 6,
        align: 'center',
      })}
      {showConfirmPasswordForm ? (
        <ConfirmPasswordForm
          form={form}
          onFinish={(values) =>
            onSubmitChange({
              setIsLoading: setIsLogging,
              confirmPassword: () => confirmPassword(pkid, values),
              onLogout: () => logout(),
              onClose: () => {
                setIsLogging(false)
                setShowConfirmPasswordForm(false)
                form.resetFields()
              },
              onRedirect: () => history.push('/login'),
            })
          }
          loading={isLogging}
        />
      ) : (
        <LoginForm
          form={form}
          onFinish={(values) =>
            onSubmitLogin({
              setIsLoading: setIsLogging,
              onLogin: () => onLogin({ email: values.email, password: values.password }),
              whoAmI,
              onConfirm,
              onRedirect: () => history.push('/login'),
              onShowCode: () => setShowQrModal(true),
              email: values.email,
              sendEmailSelfResetPassword: () => sendEmailSelfResetPassword({ email: values.email }),
            })
          }
          loading={isLogging}
        />
      )}
    </>
  )

  const renderEmailConfirmForm = () => (
    <>
      {renderLoginAndResetFormTitle({
        title: 'LOGIN_FORM_RESET_PASSWORD_TITLE',
        level: 6,
        align: 'center',
      })}
      <EmailConfirmForm />
    </>
  )

  const renderForgotPasswordButton = () => (
    <div className="forgot-password-container">
      <Button.Link
        onClick={() => setShowEmailConfirmForm(!showEmailConfirmForm)}
        title={
          !showEmailConfirmForm ? 'LOGIN_FORGOT_PASSWORD_ACTION' : 'LOGIN_GO_BACK_SESSION_ACTION'
        }
      />
    </div>
  )

  return (
    <>
      <Spin spinning={isLoggingOther}>
        <div className="plika-login-container">
          <Row>
            <Col span={10} offset={1} className="brand-hero">
              {renderLoginWelcome()}
            </Col>
            <Col
              span={10}
              offset={3}
              className={`login-form-container ${
                tenantIdMicrosoftLogin.tenantId === tenantId ||
                dominioMicrosoftLogin.cliente === subdomain
                  ? 'footer-layout'
                  : ''
              }`}
            >
              <div className={showConfirmPasswordForm ? 'signin-sector' : 'login-sector'}>
                {(tenantIdMicrosoftLogin.tenantId !== tenantId &&
                  dominioMicrosoftLogin.cliente !== subdomain) ||
                (tenantIdMicrosoftLogin.tenantId === null && tenantId === null) ? (
                  <>
                    {!showEmailConfirmForm ? renderConfirmOrLoginForm() : renderEmailConfirmForm()}
                    {!showConfirmPasswordForm && (
                      <>
                        {renderForgotPasswordButton()}
                        <Divider plain>{t('LABEL_OR')}</Divider>
                        <LoginOtherContainer
                          tenantIdMicrosoftLogin={tenantIdMicrosoftLogin.tenantId}
                          dominioMicrosoftLogin={dominioMicrosoftLogin.cliente !== subdomain}
                          tenantIdByDominio={dominioMicrosoftLogin.tenantId}
                        />
                        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                          <Button.Link
                            title="SECURITY_POLICY_TERMS_BUTTON_TITLE"
                            onClick={() => window.open('/politicayterminos', '_blank')}
                            style={{ marginTop: 15 }}
                          />
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                      {renderLoginAndResetFormTitle({
                        title: 'LOGIN_FORM_TITLE',
                        level: 6,
                        align: 'center',
                      })}
                      <LoginOtherContainer
                        tenantIdMicrosoftLogin={tenantIdMicrosoftLogin.tenantId}
                        dominioMicrosoftLogin={dominioMicrosoftLogin.cliente !== subdomain}
                        tenantIdByDominio={dominioMicrosoftLogin.tenantId}
                      />
                    </Space>
                    <div
                      className="bottom-block"
                      style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
                    >
                      <Button.Link
                        title="SECURITY_POLICY_TERMS_BUTTON_TITLE"
                        onClick={() => window.open('/politicayterminos', '_blank')}
                        style={{ marginTop: 15 }}
                      />
                    </div>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Spin>
      <AssociatedAccountModal
        visible={showAccountsModal}
        onCancel={() => {
          setShowAccountsModal(false)
          setIsLoggingOther(false)
        }}
      />
      <CodeModal visible={showQrModal} onCancel={() => setShowQrModal(false)} />
    </>
  )
}

const mapStateToProps = (state) => ({
  loggedUser: login.selectors.getWhoAmI(state),
})

const mapDispatchToProps = {
  onLogin: login.actions.onLogin,
  onLogout: login.actions.onLogout,
  logout: login.actions.logout,
  whoAmI: login.actions.whoAmI,
  confirmPassword: login.actions.confirmPassword,
  googleCallback: login.actions.googleCallback,
  fetchLoginOtherAccounts: login.actions.fetchLoginOtherAccounts,
  azureCallback: login.actions.azureCallback,
  sendEmailSelfResetPassword: login.actions.sendEmailSelfResetPassword,
  setAuthenticatedTrue: login.actions.setAuthenticatedTrue,
}

export default connect(mapStateToProps, mapDispatchToProps)(Login)
